import { Button } from "components/Button";
import { Card } from "components/Card";
import { CheckBox } from "components/CheckBox";
import { InputFormField } from "components/InputFormField";
import { Loading } from "components/Loading";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import {
  ProfilePhotoUpload,
  ProfilePhotoUploadRef,
} from "components/ProfilePhotoUpload";
import { Separator } from "components/Separator";
import { TextFormField } from "components/TextFormField";
import React, { ChangeEvent, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { RegistrationProps } from "../RegistrationNew/useRegistrationNew";
import { PersonSearchModal } from "./PersonSearchModal";
import { PersonSelectPayload } from "./PersonSearchModal/usePersonSearch";
import {
  Container,
  CustomerInfoBox,
  FormActions,
  SideContainer,
  Subtitle,
  Wrapper,
} from "./styles";

interface RegistrationFormProps {
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  inputsValues: RegistrationProps;
  touched: any;
  errors: any;
  hasError: boolean;
  isLoading: boolean;
  isEditing?: boolean;
  eventId?: string;
  registrationId?: string;
  customerId?: string | null;
  onPhotoUploaded?: (photoData: any) => void;
  onPhotoRemoved?: () => void;
  onSubmitWithPhoto?: (
    uploadPhotoCallback: (registrationId?: string) => Promise<boolean>
  ) => void;
  onPersonSelect?: (payload: PersonSelectPayload) => void;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  handleChange,
  inputsValues,
  touched,
  errors,
  handleSubmit,
  hasError,
  isLoading,
  isEditing = false,
  eventId,
  registrationId,
  customerId,
  onPhotoUploaded,
  onPhotoRemoved,
  onSubmitWithPhoto,
  onPersonSelect,
}) => {
  const { push } = useHistory();
  const profilePhotoRef = useRef<ProfilePhotoUploadRef>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  if (isLoading) {
    return <Loading />;
  }

  if (hasError) {
    return (
      <PlaceHolderNotExists title="Erro ao tentar conectar com o servidor" />
    );
  }

  const handleCheckboxChange =
    (name: string) => (checkboxEvent: { checked: boolean; value: any }) => {
      const event = {
        target: {
          name,
          value: checkboxEvent.value,
          type: "checkbox",
          checked: checkboxEvent.checked,
        },
      } as ChangeEvent<HTMLInputElement>;
      handleChange(event);
    };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onSubmitWithPhoto) {
      if (profilePhotoRef.current) {
        // Flow: RegistrationEdit com ProfilePhotoUpload
        console.log("Using old flow (RegistrationEdit)");
        onSubmitWithPhoto(async (overrideRegistrationId?: string) => {
          if (profilePhotoRef.current?.hasSelectedPhoto()) {
            return await profilePhotoRef.current.uploadPhoto(
              overrideRegistrationId
            );
          }
          return true; // No photo selected, continue with form submission
        });
      }
    } else {
      // Fallback to original submit if no photo upload callback
      handleSubmit();
    }
  };

  return (
    <>
      <Container>
        <SideContainer>
          <form onSubmit={handleFormSubmit}>
            <Card title="Dados do Cliente" typography="subtitle-medium">
              {!isEditing && onPersonSelect && (
                <Wrapper>
                  <Button
                    label="Buscar Pessoa"
                    typeBtn="outline"
                    onClick={() => setIsSearchModalOpen(true)}
                    type="button"
                  />
                </Wrapper>
              )}
              <Wrapper>
                <InputFormField
                  name="customerCpf"
                  label="CPF do Cliente"
                  error={touched.customerCpf && !!errors.customerCpf}
                  mensageError={errors.customerCpf || "CPF inválido"}
                  value={inputsValues.customerCpf}
                  onChange={handleChange}
                  block
                  placeholder="000.000.000-00"
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="importData.email"
                  label="Email"
                  error={touched.importData?.email && !!errors.importData?.email}
                  mensageError={errors.importData?.email || "Email inválido"}
                  value={inputsValues.importData?.email || ""}
                  onChange={handleChange}
                  block
                  placeholder="email@exemplo.com"
                />

                <InputFormField
                  name="importData.phone"
                  label="Telefone"
                  error={touched.importData?.phone && !!errors.importData?.phone}
                  mensageError={errors.importData?.phone || "Telefone inválido"}
                  value={inputsValues.importData?.phone || ""}
                  onChange={handleChange}
                  block
                  placeholder="(XX) 9XXXX-XXXX"
                />
              </Wrapper>

              {isEditing && customerId && (
                <Wrapper>
                  <CustomerInfoBox>
                    <div className="customer-info">
                      <strong>ID do Cliente:</strong>
                      <span>{customerId}</span>
                    </div>
                    <Button
                      label="Ver Detalhes do Cliente"
                      typeBtn="blue"
                      size="medium"
                      onClick={() =>
                        push(`/home/customer-details/${customerId}`)
                      }
                      type="button"
                    />
                  </CustomerInfoBox>
                </Wrapper>
              )}
            </Card>

            <Card title="Perfil da Inscrição" typography="subtitle-medium">
              <Wrapper>
                <InputFormField
                  name="profile.name"
                  label="Nome"
                  error={touched.profile?.name && !!errors.profile?.name}
                  mensageError="Nome é obrigatório"
                  value={inputsValues.profile.name}
                  onChange={handleChange}
                  block
                  required
                />

                <InputFormField
                  name="profile.company"
                  label="Empresa"
                  error={touched.profile?.company && !!errors.profile?.company}
                  mensageError="Empresa é obrigatória"
                  value={inputsValues.profile.company}
                  onChange={handleChange}
                  block
                  required
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="profile.position"
                  label="Cargo"
                  error={
                    touched.profile?.position && !!errors.profile?.position
                  }
                  mensageError="Cargo é obrigatório"
                  value={inputsValues.profile.position}
                  onChange={handleChange}
                  block
                  required
                />
              </Wrapper>

              <Wrapper>
                <InputFormField
                  name="profile.displayPosition"
                  label="Ordem de Exibição"
                  type="number"
                  error={
                    touched.profile?.displayPosition &&
                    !!errors.profile?.displayPosition
                  }
                  mensageError={
                    errors.profile?.displayPosition
                      ? String(errors.profile?.displayPosition)
                      : undefined
                  }
                  value={inputsValues.profile.displayPosition?.toString() || ""}
                  onChange={handleChange}
                  block
                />
              </Wrapper>

              <Wrapper>
                <TextFormField
                  name="profile.bio"
                  label="Biografia"
                  error={touched.profile?.bio && !!errors.profile?.bio}
                  mensageError="Biografia é obrigatória"
                  value={inputsValues.profile.bio}
                  onChange={handleChange}
                  block
                  rows={4}
                  placeholder="Descreva a biografia do participante..."
                />
              </Wrapper>

              {isEditing && (
                <Wrapper>
                  <ProfilePhotoUpload
                    ref={profilePhotoRef}
                    registrationId={registrationId}
                    currentPhotoUrl={inputsValues.profile?.photo?.signedFileUrl}
                    onPhotoUploaded={onPhotoUploaded}
                    onPhotoRemoved={onPhotoRemoved}
                    label="Foto de Perfil"
                  />
                </Wrapper>
              )}

              <Separator maxWidth="100%" marginY="0" />
              <Subtitle>Configurações</Subtitle>

              <Wrapper>
                <CheckBox
                  name="profile.isSpeaker"
                  label="É Palestrante"
                  value={inputsValues.profile.isSpeaker}
                  checked={inputsValues.profile.isSpeaker}
                  onChange={handleCheckboxChange("profile.isSpeaker")}
                />

                <CheckBox
                  name="profile.isVisible"
                  label="Perfil Visível"
                  value={inputsValues.profile.isVisible}
                  checked={inputsValues.profile.isVisible}
                  onChange={handleCheckboxChange("profile.isVisible")}
                />
              </Wrapper>
            </Card>

            <FormActions>
              <div />
              <div style={{ display: "flex", gap: "1rem" }}>
                <Button
                  label="Cancelar"
                  typeBtn="outline"
                  onClick={() => push(`/events/${eventId}?tab=Inscrições`)}
                  type="button"
                />
                <Button
                  label={isEditing ? "Salvar Alterações" : "Criar Inscrição"}
                  typeBtn="blue"
                  type="submit"
                  load={isLoading}
                  disabled={isLoading}
                />
              </div>
            </FormActions>
          </form>
        </SideContainer>
      </Container>

      {!isEditing && onPersonSelect && (
        <PersonSearchModal
          open={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onSelect={(payload) => {
            onPersonSelect(payload);
            setIsSearchModalOpen(false);
          }}
        />
      )}
    </>
  );
};
